import { useState, useEffect } from "react";
import type { FC, KeyboardEvent, ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import styles from "./CategoryList.module.scss";
import { useNavigate, useSearchParams } from "react-router-dom";
import { formatName } from "@/utils/formatName";
import { getSiteConfig } from "@/config/siteConfigs";

const siteConfig = getSiteConfig();

interface CategoryListProps {
  isMenu?: boolean;
  onClose?: () => void;
}

type CategoryData = Record<string, string>;
type CategoryMap = Record<string, CategoryData>;

const fetchCategories = async (
  table: "goods" | "service",
  catid?: string
): Promise<CategoryData> => {
  const params: Record<string, string> = {
    type: "category",
    legid: "1",
    language: siteConfig.language,
  };

  if (table === "service") {
    params.table = "service";
  }

  if (catid !== undefined) params.catid = catid;

  const response = await fetch("https://crm.hixa.eu/external/goodsapi/", {
    method: "POST",
    headers: {
      Authorization: "Bearer DH345DFGDGb32FDsf03876SDFSdf13",
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "application/json",
    },
    body: new URLSearchParams(params),
  });

  const data = await response.json();
  return data.success && data.data ? data.data : {};
};

const CategoryList: FC<CategoryListProps> = ({ isMenu = false, onClose }) => {
  const { t } = useTranslation();
  const [goodsCategories, setGoodsCategories] = useState<CategoryData>({});
  const [goodsSubcategories, setGoodsSubcategories] = useState<CategoryMap>({});

  const [serviceCategories, setServiceCategories] = useState<CategoryData>({});
  const [serviceSubcategories, setServiceSubcategories] = useState<CategoryMap>(
    {}
  );

  // ✅ ВИПРАВЛЕНІ РЯДКИ:
  const [expandedGoodsCategory, setExpandedGoodsCategory] = useState<
    string | null
  >(null);
  const [expandedServiceCategory, setExpandedServiceCategory] = useState<
    string | null
  >(null);
  const [hoveredGoodsParent, setHoveredGoodsParent] = useState<string | null>(
    null
  );
  const [hoveredServiceParent, setHoveredServiceParent] = useState<
    string | null
  >(null);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const selectedCategory = searchParams.get("category") || null;
  const initialQuery = searchParams.get("search") || "";
  const [query, setQuery] = useState(initialQuery);

  const updateCategory = (
    categoryId: string,
    categoryName: string,
    type: "goods" | "service"
  ) => {
    const formattedName = formatName(categoryName);
    const params = new URLSearchParams();

    if (query.trim()) {
      params.set("search", query.trim());
    }

    const queryString = params.toString();
    const section = type === "goods" ? "products" : "services";
    const url = queryString
      ? `/shop/${section}/${categoryId}/${formattedName}?${queryString}`
      : `/shop/${section}/${categoryId}/${formattedName}`;

    navigate(url, { replace: true });
  };

  useEffect(() => {
    const loadInitial = async () => {
      const goods = await fetchCategories("goods");
      const services = await fetchCategories("service");
      setGoodsCategories(goods);
      setServiceCategories(services);
    };
    loadInitial();
  }, []);

  useEffect(() => {
    const urlQuery = searchParams.get("search") || "";
    setQuery(urlQuery);
  }, [searchParams]);

  useEffect(() => {
    if (isMenu) return;
    const ids = Object.keys(goodsCategories);
    if (!ids.length) return;
    const first = ids[0];
    if (!hoveredGoodsParent) {
      setHoveredGoodsParent(first);
    }
    if (first && !goodsSubcategories[first]) {
      fetchCategories("goods", first).then((subs) =>
        setGoodsSubcategories((prev) => ({ ...prev, [first]: subs }))
      );
    }
  }, [goodsCategories, isMenu, hoveredGoodsParent, goodsSubcategories]);

  useEffect(() => {
    if (isMenu) return;
    const ids = Object.keys(serviceCategories);
    if (!ids.length) return;
    const first = ids[0];
    if (!hoveredServiceParent) {
      setHoveredServiceParent(first);
    }
    if (first && !serviceSubcategories[first]) {
      fetchCategories("service", first).then((subs) =>
        setServiceSubcategories((prev) => ({ ...prev, [first]: subs }))
      );
    }
  }, [serviceCategories, isMenu, hoveredServiceParent, serviceSubcategories]);

  const handleToggleGoods = async (id: string) => {
    if (expandedGoodsCategory === id) {
      setExpandedGoodsCategory(null);
    } else {
      if (!goodsSubcategories[id]) {
        const subs = await fetchCategories("goods", id);
        setGoodsSubcategories((prev) => ({ ...prev, [id]: subs }));
      }
      setExpandedGoodsCategory(id);
    }
  };

  const handleToggleService = async (id: string) => {
    if (expandedServiceCategory === id) {
      setExpandedServiceCategory(null);
    } else {
      if (!serviceSubcategories[id]) {
        const subs = await fetchCategories("service", id);
        setServiceSubcategories((prev) => ({ ...prev, [id]: subs }));
      }
      setExpandedServiceCategory(id);
    }
  };

  const handleGoodsParentHover = async (id: string) => {
    setHoveredGoodsParent(id);
    if (!goodsSubcategories[id]) {
      const subs = await fetchCategories("goods", id);
      setGoodsSubcategories((prev) => ({ ...prev, [id]: subs }));
    }
  };

  const handleServiceParentHover = async (id: string) => {
    setHoveredServiceParent(id);
    if (!serviceSubcategories[id]) {
      const subs = await fetchCategories("service", id);
      setServiceSubcategories((prev) => ({ ...prev, [id]: subs }));
    }
  };

  const handleSubClick = (
    subId: string,
    subName: string,
    type: "goods" | "service"
  ) => {
    updateCategory(subId, subName, type);
    setHoveredGoodsParent(null);
    setHoveredServiceParent(null);
  };

  const handleSearch = () => {
    const trimmed = query.trim();
    const params = new URLSearchParams();

    if (trimmed) {
      params.set("search", trimmed);
    }

    if (selectedCategory) {
      params.set("category", selectedCategory);
    }

    navigate(`/shop?${params.toString()}`);
  };

  const applyBtn = () => {
    handleSearch();
    onClose?.();
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleSearch();
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
  };

  const handleReset = () => {
    setQuery("");
    navigate("/shop");
  };

  return (
    <div className={isMenu ? styles.categoryBlockMenu : styles.categoryBlock}>
      <div className={styles.filterBlock}>
        <div className={` textBasic ${styles.filterTitle}`}>
          {t("shop.filter")}
        </div>
        <div className={styles.searchBar}>
          <input
            type="text"
            value={query}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={t("shop.searchPlaceholder")}
          />
          <button
            className={styles.button}
            aria-label={t("shop.startSearch")}
            onClick={handleSearch}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 512 512"
              aria-hidden="true"
            >
              <path d="M495,466.2L377.2,348.4c29.2-35.6,46.8-81.2,46.8-130.9C424,103.5,331.5,11,217.5,11C103.4,11,11,103.5,11,217.5S103.4,424,217.5,424c49.7,0,95.2-17.5,130.8-46.7L466.1,495c8,8,20.9,8,28.9,0C503,487.1,503,474.1,495,466.2z M217.5,382.9C126.2,382.9,52,308.7,52,217.5S126.2,52,217.5,52C308.7,52,383,126.3,383,217.5S308.7,382.9,217.5,382.9z" />
            </svg>
          </button>
        </div>
      </div>

      <div className={`textBasic ${styles.filterTitle}`}>{t("shop.goods")}</div>

      {isMenu && (
        <ul className={styles.categoryList}>
          {Object.entries(goodsCategories).map(([id, name]) => {
            const isExpanded = expandedGoodsCategory === id;
            return (
              <li
                key={id}
                className={`${styles.categoryItemWrapper} ${
                  isExpanded ? styles.expanded : styles.collapsed
                }`}
              >
                <div className={styles.categoryItem}>
                  <span
                    onClick={() => {
                      updateCategory(id, name, "goods");
                      onClose?.();
                    }}
                  >
                    {name}
                  </span>
                  <button
                    className={styles.faqItemButton}
                    onClick={() => handleToggleGoods(id)}
                    aria-label={
                      isExpanded ? t("common.collapse") : t("common.expand")
                    }
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="15"
                      height="15"
                      viewBox="0 0 15 15"
                      fill="none"
                      className={styles.arrowIcon}
                      aria-hidden="true"
                    >
                      <path
                        d="M1.50002 4L6.00002 8L10.5 4"
                        strokeWidth="1.5"
                        stroke="currentColor"
                      />
                    </svg>
                  </button>
                </div>
                {isExpanded && goodsSubcategories[id] && (
                  <ul>
                    {Object.entries(goodsSubcategories[id]).map(
                      ([subId, subName]) => (
                        <li
                          key={subId}
                          className={`${styles.subItem} ${
                            selectedCategory === subId ? styles.active : ""
                          }`}
                          onClick={() => {
                            updateCategory(subId, subName, "goods");
                            onClose?.();
                          }}
                        >
                          {subName}
                        </li>
                      )
                    )}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {!isMenu && (
        <ul className={styles.flyoutList}>
          {Object.entries(goodsCategories).map(([id, name]) => (
            <li
              key={id}
              className={styles.parentWrapper}
              onMouseEnter={() => handleGoodsParentHover(id)}
            >
              <button
                type="button"
                className={styles.parentItem}
                onClick={() => updateCategory(id, name, "goods")}
              >
                {name}
              </button>
              <ul
                className={styles.childFlyout}
                style={{
                  display: hoveredGoodsParent === id ? "block" : "none",
                }}
              >
                {goodsSubcategories[id] ? (
                  Object.entries(goodsSubcategories[id]).map(
                    ([subId, subName]) => (
                      <li key={subId}>
                        <button
                          type="button"
                          className={`${styles.subItem} ${
                            selectedCategory === subId ? styles.active : ""
                          }`}
                          onClick={() =>
                            handleSubClick(subId, subName, "goods")
                          }
                        >
                          {subName}
                        </button>
                      </li>
                    )
                  )
                ) : (
                  <li className={styles.loading}>{t("common.loading")}</li>
                )}
              </ul>
            </li>
          ))}
        </ul>
      )}

      <div
        className={`textBasic ${styles.filterTitle}`}
        style={{ marginTop: "20px" }}
      >
        {t("shop.services")}
      </div>

      {isMenu && (
        <ul className={styles.categoryList}>
          {Object.entries(serviceCategories).map(([id, name]) => {
            const isExpanded = expandedServiceCategory === id;
            return (
              <li
                key={id}
                className={`${styles.categoryItemWrapper} ${
                  isExpanded ? styles.expanded : styles.collapsed
                }`}
              >
                <div className={styles.categoryItem}>
                  <span
                    onClick={() => {
                      updateCategory(id, name, "service");
                      onClose?.();
                    }}
                  >
                    {name}
                  </span>
                  <button
                    className={styles.faqItemButton}
                    onClick={() => handleToggleService(id)}
                    aria-label={
                      isExpanded ? t("common.collapse") : t("common.expand")
                    }
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="15"
                      height="15"
                      viewBox="0 0 15 15"
                      fill="none"
                      className={styles.arrowIcon}
                      aria-hidden="true"
                    >
                      <path
                        d="M1.50002 4L6.00002 8L10.5 4"
                        strokeWidth="1.5"
                        stroke="currentColor"
                      />
                    </svg>
                  </button>
                </div>
                {isExpanded && serviceSubcategories[id] && (
                  <ul>
                    {Object.entries(serviceSubcategories[id]).map(
                      ([subId, subName]) => (
                        <li
                          key={subId}
                          className={`${styles.subItem} ${
                            selectedCategory === subId ? styles.active : ""
                          }`}
                          onClick={() => {
                            updateCategory(subId, subName, "service");
                            onClose?.();
                          }}
                        >
                          {subName}
                        </li>
                      )
                    )}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {!isMenu && (
        <ul className={styles.flyoutList}>
          {Object.entries(serviceCategories).map(([id, name]) => (
            <li
              key={id}
              className={styles.parentWrapper}
              onMouseEnter={() => handleServiceParentHover(id)}
            >
              <button
                type="button"
                className={styles.parentItem}
                onClick={() => updateCategory(id, name, "service")}
              >
                {name}
              </button>
              <ul
                className={styles.childFlyout}
                style={{
                  display: hoveredServiceParent === id ? "block" : "none",
                }}
              >
                {serviceSubcategories[id] ? (
                  Object.entries(serviceSubcategories[id]).map(
                    ([subId, subName]) => (
                      <li key={subId}>
                        <button
                          type="button"
                          className={`${styles.subItem} ${
                            selectedCategory === subId ? styles.active : ""
                          }`}
                          onClick={() =>
                            handleSubClick(subId, subName, "service")
                          }
                        >
                          {subName}
                        </button>
                      </li>
                    )
                  )
                ) : (
                  <li className={styles.loading}>{t("common.loading")}</li>
                )}
              </ul>
            </li>
          ))}
        </ul>
      )}

      <button className={styles.resetBtn} onClick={handleReset}>
        {t("shop.reset")}
      </button>
      {isMenu && (
        <button className={styles.applyBtn} onClick={applyBtn}>
          {t("shop.applyFilter")}
        </button>
      )}
    </div>
  );
};

export default CategoryList;
